import { documentService } from "../../services/documentService";
import { vollmachtService, VollmachtFunktionFehlt } from "../../services/vollmachtService";
import type { SignierlinkAnzeige } from "./signierlink";
import {
  gewaehlteArten,
  vollmachtAblageOrdner,
  vollmachtLabel,
  type VollmachtAuswahl,
  type VollmachtDto,
} from "./vollmachtTypes";

type Abhaengigkeiten = {
  anlegen: typeof vollmachtService.anlegen;
  verknuepfen: typeof vollmachtService.verknuepfen;
  signierlink: typeof vollmachtService.signierlink;
  upload: typeof documentService.upload;
};

const standard: Abhaengigkeiten = {
  anlegen: (...a) => vollmachtService.anlegen(...a),
  verknuepfen: (...a) => vollmachtService.verknuepfen(...a),
  signierlink: (...a) => vollmachtService.signierlink(...a),
  upload: (p) => documentService.upload(p),
};

const fehlerText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/**
 * Nach dem Anlegen des Kontakts: gewählte Vollmachten anlegen und erzeugen,
 * vorab gewählte Dateien ablegen und verknüpfen, gewünschte Signierlinks
 * erzeugen. Nichts davon bricht die Anlage ab — der Kontakt ist schon
 * gespeichert. Zurück kommt, was dem Nutzer zu sagen ist: Hinweise (Fehler)
 * und die erzeugten Signierlinks (beides leer = nichts zu sagen).
 *
 * Bewusst ein Folgeaufruf statt eines Schlüssels im Anlage-Payload (Plan §2.7):
 * Die Uploads brauchen ohnehin die Kontakt-Id, und `POST /contact` verliert
 * Zusatzschlüssel.
 */
export async function vollmachtenNachAnlage(
  contactId: number,
  auswahl: VollmachtAuswahl[],
  deps: Abhaengigkeiten = standard,
): Promise<{ meldungen: string[]; links: SignierlinkAnzeige[] }> {
  const arten = gewaehlteArten(auswahl);
  const meldungen: string[] = [];
  const links: SignierlinkAnzeige[] = [];
  // Die Karte sperrt Hochladen ohne gewählte Art; kommt trotzdem eine Datei
  // ohne Art an, wird sie wenigstens gemeldet statt still verworfen.
  for (const a of auswahl) {
    if (!a.art && a.datei) {
      meldungen.push(`${a.datei.name} wurde nicht abgelegt, weil keine Vollmacht gewählt war.`);
    }
  }
  if (!arten.length) return { meldungen, links };

  let zeilen: VollmachtDto[] | null = null;
  let fehlt = false;
  try {
    zeilen = await deps.anlegen(contactId, arten);
    for (const z of zeilen) {
      if (z.fehler) meldungen.push(`${z.titel}: ${z.fehler}`);
    }
  } catch (err) {
    if (err instanceof VollmachtFunktionFehlt) {
      fehlt = true;
      meldungen.push("Die Vollmachten konnten noch nicht erzeugt werden, weil die Funktion auf dem Server fehlt.");
    } else {
      meldungen.push(`Die Vollmachten konnten nicht erzeugt werden: ${fehlerText(err)}`);
    }
  }
  const zeileZu = (art: string) => zeilen?.find((z) => z.art === art) ?? null;

  // Dateien gehen nie verloren: `documents/upload` gibt es auch ohne neues Backend.
  const hochgeladen = new Set<string>();
  for (const a of auswahl) {
    if (!a.art || !a.datei) continue;
    const titel = vollmachtLabel(a.art);
    let documentId: number;
    try {
      const doc = await deps.upload({
        file: a.datei,
        contactId,
        category: "Vollmacht",
        relativePath: vollmachtAblageOrdner(a.art),
        tags: ["vollmacht", a.art],
      });
      documentId = doc.id;
      hochgeladen.add(a.art);
    } catch (err) {
      meldungen.push(`${titel}: ${a.datei.name} konnte nicht abgelegt werden (${fehlerText(err)}).`);
      continue;
    }
    const zeile = zeileZu(a.art);
    if (!zeile) {
      if (fehlt) meldungen.push(`${titel}: ${a.datei.name} liegt in der Akte, ist aber noch nicht als Vollmacht verknüpft.`);
      continue;
    }
    try {
      await deps.verknuepfen(contactId, zeile.id, documentId);
    } catch (err) {
      meldungen.push(`${titel}: Datei abgelegt, Verknüpfung fehlgeschlagen (${fehlerText(err)}).`);
    }
  }

  for (const a of auswahl) {
    if (!a.art || !a.signierenNachAnlage || hochgeladen.has(a.art)) continue;
    const zeile = zeileZu(a.art);
    if (!zeile) continue;
    try {
      const { url, expiresAt } = await deps.signierlink(contactId, zeile.id);
      links.push({ titel: zeile.titel, url, expiresAt: String(expiresAt ?? "") });
    } catch (err) {
      meldungen.push(`${zeile.titel}: Signierlink konnte nicht erzeugt werden (${fehlerText(err)}).`);
    }
  }
  return { meldungen, links };
}

/**
 * Eine Zeile im `runSubmit`: ausführen und EINMAL gesammelt melden — im
 * eigenen Popup statt `alert` (User 28.09.2026). Erfüllt sich erst beim
 * Schließen, wie vorher der Browser-Dialog.
 */
export async function vollmachtenNachAnlageMelden(contactId: number, auswahl: VollmachtAuswahl[]): Promise<void> {
  const { meldungen, links } = await vollmachtenNachAnlage(contactId, auswahl);
  if (!meldungen.length && !links.length) return;
  // Dynamisch: hält React-DOM-Wurzel und Redesign-CSS aus dem Test dieser Datei.
  const { zeigeSignierlinkDialog } = await import("./SignierlinkDialog");
  await zeigeSignierlinkDialog({
    ueberschrift: "Kontakt angelegt",
    links,
    hinweise: meldungen,
  });
}
